import React, { useRef, useState, useCallback } from 'react';

export interface SliderPoint {
  x: number;
  y: number;
  delay_ms: number;
}

interface LocalSliderProps {
  onComplete: (points: SliderPoint[]) => void;
  disabled?: boolean;
  buttonSize?: number;  // 真实按钮宽度，从后端传入
  trackHeight?: number; // 真实轨道高度，从后端传入
}

const DEFAULT_BUTTON_SIZE = 36;
const DEFAULT_TRACK_HEIGHT = 40;
const GRAB_SLACK = 8;

export const LocalSlider: React.FC<LocalSliderProps> = ({
  onComplete,
  disabled = false,
  buttonSize = DEFAULT_BUTTON_SIZE,
  trackHeight = DEFAULT_TRACK_HEIGHT
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(0); // 0-1 归一化位置，0 表示按钮在最左
  const pointsRef = useRef<SliderPoint[]>([]);
  const lastTimeRef = useRef<number>(0);
  // 按下时指针与按钮中心的偏移，保证拖动时按钮不跳变
  const grabOffsetRef = useRef<number>(0);

  // 使用从后端传入的真实按钮宽度
  const BUTTON_SIZE = buttonSize;

  // 按钮中心的可行程像素宽度（两端各留半个按钮）
  const travelPx = (rect: DOMRect) => Math.max(1, rect.width - BUTTON_SIZE);

  const positionFromClientX = useCallback((clientX: number, rect: DOMRect) => {
    const center = clientX - grabOffsetRef.current - rect.left;
    return Math.max(0, Math.min(1, (center - BUTTON_SIZE / 2) / travelPx(rect)));
  }, []);

  const handlePointerDown = useCallback((event: React.PointerEvent) => {
    if (disabled) return;

    const track = trackRef.current;
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const buttonCenter = rect.left + BUTTON_SIZE / 2 + position * travelPx(rect);

    // 只允许从按钮上起拖：否则 points[0] 会把轨道中段当成增量基准，
    // 后端重放时 delta 偏小，滑块永远走不到终点。
    if (Math.abs(event.clientX - buttonCenter) > BUTTON_SIZE / 2 + GRAB_SLACK) return;

    event.preventDefault();
    event.stopPropagation();

    track.setPointerCapture(event.pointerId);
    grabOffsetRef.current = event.clientX - buttonCenter;

    setIsDragging(true);

    // 起始点固定为当前位置（通常为 0），作为后端 delta 的基准
    lastTimeRef.current = performance.now();
    pointsRef.current = [{ x: position, y: 0.5, delay_ms: 0 }];
  }, [disabled, position]);

  const handlePointerMove = useCallback((event: React.PointerEvent) => {
    if (!isDragging || disabled) return;

    const track = trackRef.current;
    if (!track) return;

    event.preventDefault();

    const x = positionFromClientX(event.clientX, track.getBoundingClientRect());
    setPosition(x);

    const now = performance.now();
    const delay_ms = now - lastTimeRef.current;
    lastTimeRef.current = now;
    pointsRef.current.push({ x, y: 0.5, delay_ms });
  }, [isDragging, disabled, positionFromClientX]);

  const handlePointerUp = useCallback((event: React.PointerEvent) => {
    if (!isDragging) return;

    const track = trackRef.current;
    if (!track) return;

    event.preventDefault();

    if (track.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }

    const x = positionFromClientX(event.clientX, track.getBoundingClientRect());
    setPosition(x);
    setIsDragging(false);

    const points = pointsRef.current;
    const delay_ms = performance.now() - lastTimeRef.current;
    points.push({ x, y: 0.5, delay_ms });
    pointsRef.current = [];

    // 原地点一下不算拖动，交给上层提示，避免提交无效轨迹
    onComplete(points);
  }, [isDragging, onComplete, positionFromClientX]);

  const handlePointerCancel = useCallback((event: React.PointerEvent) => {
    if (!isDragging) return;

    const track = trackRef.current;
    if (track && track.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }

    setIsDragging(false);
    setPosition(0);
    pointsRef.current = [];
  }, [isDragging]);

  return (
    <div className="local-slider-container">
      <div className="local-slider-hint">
        请按住滑块，拖动到最右边
      </div>

      <div
        ref={trackRef}
        className={`local-slider-track ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        style={{
          position: 'relative',
          width: '100%',
          height: `${trackHeight}px`,
          backgroundColor: '#f0f0f0',
          borderRadius: `${trackHeight / 2}px`,
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        {/* 已拖动的进度条 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: `calc(${BUTTON_SIZE / 2}px + ${position} * (100% - ${BUTTON_SIZE}px))`,
            height: '100%',
            backgroundColor: isDragging ? '#4CAF50' : '#81C784',
            borderRadius: `${trackHeight / 2}px`,
            transition: isDragging ? 'none' : 'width 0.2s',
          }}
        />

        {/* 滑块按钮：位置与 positionFromClientX 的行程几何保持一致 */}
        <div
          style={{
            position: 'absolute',
            left: `calc(${BUTTON_SIZE / 2}px + ${position} * (100% - ${BUTTON_SIZE}px))`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${BUTTON_SIZE}px`,
            height: `${BUTTON_SIZE}px`,
            backgroundColor: '#fff',
            borderRadius: '50%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: isDragging ? 'none' : 'left 0.2s',
            cursor: disabled ? 'not-allowed' : 'grab',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 6L12 10L7 14" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      <style>{`
        .local-slider-hint {
          text-align: center;
          color: #666;
          font-size: 14px;
          margin-bottom: 12px;
        }

        .local-slider-track.dragging {
          cursor: grabbing !important;
        }

        .local-slider-track.disabled {
          opacity: 0.5;
        }
      `}</style>
    </div>
  );
};
